import React from 'react';
import images from 'assets/images';
import PageLayout from 'components/common/PageLayout';

import css from './gilProfile.module.scss';

const profileLinks = [
  ['LinkedIn', 'https://www.linkedin.com/in/gilrabbi/'],
  ['Wikipedia', 'https://he.wikipedia.org/wiki/%D7%92%D7%99%D7%9C_%D7%A8%D7%91%D7%99'],
  ['Crunchbase', 'https://www.crunchbase.com/person/gil-rabbi'],
];

const milestones = [
  ['20+', 'Years building interactive technology'],
  ['600K', 'Devices synchronized simultaneously'],
  ['120+', 'Festivals and concerts using the technology'],
];

const recognition = [
  {
    source: 'Rabbi Interactive project',
    title: 'Live audience voting for Rising Star',
    description:
      'A project summary of the technology used for live audience voting on Rising Star.',
    href: 'https://rabbi.agency/project/102/Developing-Rising-Star-for-Eurovision--App',
  },
  {
    source: 'The Times of Israel',
    title: 'From interactive media products to Storycards',
    description:
      'A profile of Gil’s work in interactive media and the development of Storycards.',
    href: 'https://blogs.timesofisrael.com/tech-leaders-gil-rabbi-founder-storycards/',
  },
  {
    source: 'Authority Magazine',
    title: 'A conversation about no-code and audience engagement',
    description:
      'Gil discusses no-code technology, audience engagement and the ideas behind Storycards.',
    href: 'https://medium.com/authority-magazine/the-future-is-now-gil-rabbi-of-storycards-on-how-their-technological-innovation-will-shake-up-the-39422f9ff920',
  },
];

const GilProfile = () => (
  <PageLayout
    className={css.page}
    contentClassName={css.contentLayout}
    isFullWidth
    isWhiteHeader
    isMarketingHeader
    showFooterCta={false}
  >
    <main>
      <section className={css.hero}>
        <div className={css.heroContent}>
          <h1>Gil Rabbi builds technology that lets audiences take part.</h1>
          <p className={css.heroIntro}>
            For more than two decades, Gil has built participation technology for live
            television, large events and digital products. Today he leads Storycards, the
            platform that grew out of the same work.
          </p>
          <nav className={css.profileLinks} aria-label="Gil Rabbi profiles">
            {profileLinks.map(([label, href]) => (
              <a key={label} href={href} target="_blank" rel="noreferrer">
                {label} <span aria-hidden="true">↗</span>
              </a>
            ))}
          </nav>
        </div>
      </section>

      <figure className={css.featureImage}>
        <img src={images.gilRabbi} alt="Gil Rabbi" />
        <figcaption>
          From live voting to Storycards, Gil’s work has followed one thread: bringing the
          audience into the experience.
        </figcaption>
      </figure>

      <section className={css.milestones} aria-label="Career highlights">
        {milestones.map(([value, label]) => (
          <div className={css.milestone} key={value}>
            <strong>{value}</strong>
            <span>{label}</span>
          </div>
        ))}
      </section>

      <section className={css.recognitionLead} aria-labelledby="gil-recognition-title">
        <div className={css.recognitionLeadIntro}>
          <p className={css.eyebrow}>Selected honors</p>
          <h2 id="gil-recognition-title">Recognition.</h2>
        </div>
        <div className={css.recognitionHighlights}>
          <article>
            <strong>Top 100</strong>
            <p>
              Named among <b>Israel’s 100 most influential people in digital</b> in 2016.
            </p>
          </article>
          <article>
            <strong>40 Under Forty</strong>
            <p>
              Selected for ICE’s 2018 list recognizing leaders in Israel’s digital, media and
              communications industries.
            </p>
          </article>
        </div>
      </section>

      <section className={css.manifesto}>
        <p className={css.eyebrow}>The question behind the work</p>
        <h2>How do you make the audience part of the experience?</h2>
        <p>
          Gil has returned to that question throughout his career, from second-screen products
          and live voting to synchronized event technology and Storycards.
        </p>
      </section>

      <section className={css.story}>
        <header className={css.storyHeader}>
          <p className={css.eyebrow}>The story</p>
          <h2>How years of building products became Storycards.</h2>
        </header>

        <div className={css.chapters}>
          <article className={css.chapter}>
            <span>01</span>
            <div>
              <h3>Founding Rabbi Interactive</h3>
              <p>
                In 2005, Gil founded Rabbi Interactive. The company built interactive products,
                engagement platforms and real-time systems for major broadcasters, telecom
                companies and consumer brands.
              </p>
            </div>
          </article>

          <article className={css.chapter}>
            <span>02</span>
            <div>
              <h3>Bringing viewers into the broadcast</h3>
              <p>
                Gil and his team built real-time participation technology that let millions of
                people interact with live television, mobile and digital content. For the
                <strong> Rising Star</strong> television format, viewers voted during the live
                broadcast and helped choose Israel’s representatives for the
                <strong> Eurovision Song Contest</strong>. The technology was later used in other
                countries and featured in an Amazon Web Services case study about its scalability
                and reliability.
              </p>
              <p>
                He also led an Orange Mobile campaign that synchronized television commercials
                with an online game in real time. Hundreds of thousands of viewers tuned in to
                participate. The team also built companion apps for shows including
                <strong> Touch</strong>, where viewers could compete during the broadcast. These
                were early second-screen products designed to make the audience part of the show.
              </p>
            </div>
          </article>

          <article className={css.chapter}>
            <span>03</span>
            <div>
              <h3>Turning 600,000 phones into one shared experience</h3>
              <p>
                In 2019, Gil introduced <strong>Crowdr</strong>, a patented system that activated
                more than 600,000 mobile devices simultaneously during live events without
                internet or cellular connectivity. Its sound-based synchronization technology
                was used at more than 120 large festivals and concerts, bringing spectators into
                the show.
              </p>
            </div>
          </article>

          <article className={css.chapter}>
            <span>04</span>
            <div>
              <h3>From repeated work to a platform</h3>
              <p>
                In 2016, Gil and his team launched <strong>mako play</strong> with Keshet, giving
                broadcast editors a way to create second-screen activities without building a new
                app each time. It made a recurring need visible: organizations wanted interactive
                products that looked and felt different, even when much of the underlying logic
                was the same.
              </p>
              <p>
                The team built an internal platform to automate that repeated work, collect data
                and support heavy traffic. It became <a href="/about">Storycards</a>, which lets
                enterprises build, launch and improve interactive digital products without writing
                code. Current Storycards platform data shows that these products reach more than
                <strong>40 million unique users every month</strong>.
              </p>
            </div>
          </article>

          <article className={css.chapter}>
            <span>05</span>
            <div>
              <h3>Building Storycards today</h3>
              <p>
                Today, Gil focuses on how Storycards can help teams turn an idea into an
                interactive product without rebuilding the same technical foundation each time.
                His work spans product direction, AI, visual creation and the data behind each
                experience, but the question remains the same: how to make participation feel
                immediate, useful and worth the audience’s time.
              </p>
            </div>
          </article>
        </div>
      </section>

      <section className={css.recognition}>
        <header className={css.recognitionHeader}>
          <p className={css.eyebrow}>Selected reading</p>
          <h2>Projects, profiles and conversations.</h2>
        </header>

        <div className={css.recognitionGrid}>
          {recognition.map(({ source, title, description, href }) => {
            const content = (
              <>
                <span className={css.recognitionSource}>{source}</span>
                <h3>{title}</h3>
                <p>{description}</p>
                {href && <span className={css.readMore}>Read the source ↗</span>}
              </>
            );

            return href ? (
              <a
                className={css.recognitionCard}
                href={href}
                key={source}
                target="_blank"
                rel="noreferrer"
              >
                {content}
              </a>
            ) : (
              <article className={css.recognitionCard} key={source}>
                {content}
              </article>
            );
          })}
        </div>
      </section>

      <section className={css.finalCta}>
        <div>
          <p className={css.eyebrow}>Storycards today</p>
          <h2>See what Gil and the Storycards team are building now.</h2>
        </div>
        <a className={css.ctaButton} href="/">
          Explore Storycards <span aria-hidden="true">→</span>
        </a>
      </section>
    </main>
  </PageLayout>
);

export default GilProfile;
